import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel,
  InputLabel, MenuItem, Select, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import { bonusTrackerAPI } from '../../services/api';
import { BonusFrequency, lastFridayOrToday, monthEnd, periodEndProblem, periodRange, thisWeekFriday } from '../../utils/bonusPeriod';
import { BonusCategory, Campaign, EmployeeOption, employeeLabel, errText } from './bonusTypes';

interface Props {
  categories: BonusCategory[];
  campaigns: Campaign[];
  onClose: () => void;
  onCreated: (id: string, message: string) => void;
  onOpenExisting: (id: string) => void;
}

/** Add a bonus: category, weekly or monthly, the last day of the period, campaign, who pays, and the employees that qualify. */
const AddBonusDialog: React.FC<Props> = ({ categories, campaigns, onClose, onCreated, onOpenExisting }) => {
  const [categoryId, setCategoryId] = useState('');
  const [frequency, setFrequency] = useState<BonusFrequency>('MONTHLY');
  const [periodEnd, setPeriodEnd] = useState('');
  const [campaignId, setCampaignId] = useState('ALL');
  const [customerPaid, setCustomerPaid] = useState(false);
  const [concept, setConcept] = useState('');
  const [options, setOptions] = useState<EmployeeOption[]>([]);
  const [chosen, setChosen] = useState<EmployeeOption[]>([]);
  const [lineAmount, setLineAmount] = useState<Record<string, string>>({});
  const [lineConcept, setLineConcept] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [existingId, setExistingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [everyone, setEveryone] = useState(false);

  const category = categories.find((c) => c.id === categoryId);
  const problem = periodEnd ? periodEndProblem(frequency, periodEnd) : null;
  const range = periodEnd && !problem ? periodRange(frequency, periodEnd) : null;

  const search = useCallback(async (text: string) => {
    try { setOptions(await bonusTrackerAPI.employees(text, campaignId, range ? { from: range.start, to: range.end } : undefined, everyone)); } catch { /* the form still works without suggestions */ }
  }, [campaignId, range?.start, range?.end, everyone]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { void search(''); }, [search]);

  const pickCategory = (id: string) => {
    setCategoryId(id);
    const c = categories.find((x) => x.id === id);
    if (c) {
      setFrequency(c.frequency === 'WEEKLY' ? 'WEEKLY' : 'MONTHLY');
      setCustomerPaid(c.offeredBy === 'CUSTOMER');
      setPeriodEnd('');
    }
  };

  const changeFrequency = (next: BonusFrequency | null) => {
    if (!next) return;
    setFrequency(next);
    setPeriodEnd('');
  };

  const onEmployees = (list: EmployeeOption[]) => {
    setChosen(list);
    setLineAmount((prev) => {
      const next = { ...prev };
      list.forEach((u) => { if (next[u.id] === undefined) next[u.id] = category?.defaultAmount != null ? String(category.defaultAmount) : ''; });
      return next;
    });
  };

  const valid = Boolean(categoryId && periodEnd && !problem && chosen.length && chosen.every((u) => Number(lineAmount[u.id]) > 0));

  const save = async () => {
    setSaving(true);
    setError(null);
    setExistingId(null);
    try {
      const result = await bonusTrackerAPI.createRecord({
        categoryId,
        frequency,
        periodEnd,
        campaignId: campaignId === 'ALL' ? null : campaignId,
        offeredBy: customerPaid ? 'CUSTOMER' : 'IN_HOUSE',
        concept: concept || null,
        lines: chosen.map((u) => ({ employeeId: u.id, amount: Number(lineAmount[u.id]), concept: lineConcept[u.id] || null })),
      });
      const skipped = result.skipped?.length ? ` ${result.skipped.length} skipped: ${result.skipped.map((s: any) => s.reason).join('; ')}` : '';
      onCreated(result.id, `Bonus added with ${result.added} employee(s). Open it to follow the approvals.${skipped}`);
    } catch (e: any) {
      setError(errText(e));
      setExistingId(e?.response?.data?.existingId || null);
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Add bonus</DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 2, pt: '16px !important' }}>
        {error && (
          <Alert severity="error" action={existingId ? <Button color="inherit" size="small" onClick={() => onOpenExisting(existingId)}>Open it</Button> : undefined}>
            {error}
          </Alert>
        )}
        <FormControl fullWidth>
          <InputLabel>Category</InputLabel>
          <Select label="Category" value={categoryId} onChange={(e) => pickCategory(String(e.target.value))}>
            {categories.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
          </Select>
        </FormControl>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-start' }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Frequency</Typography>
            <ToggleButtonGroup exclusive size="small" value={frequency} onChange={(_e, v) => changeFrequency(v)} sx={{ display: 'flex' }}>
              <ToggleButton value="WEEKLY">Weekly</ToggleButton>
              <ToggleButton value="MONTHLY">Monthly</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            <TextField
              fullWidth
              type="date"
              label={frequency === 'WEEKLY' ? 'Last day of the week (Friday or Saturday)' : 'Last day of the month'}
              InputLabelProps={{ shrink: true }}
              value={periodEnd}
              onChange={(e) => setPeriodEnd(e.target.value)}
              error={Boolean(problem)}
              helperText={problem || (range ? `Covers ${range.start} to ${range.end}` : 'Open the calendar and pick the last day of the period.')}
            />
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              {frequency === 'WEEKLY' ? (
                <>
                  <Chip size="small" clickable label="This week's Friday" onClick={() => setPeriodEnd(thisWeekFriday())} />
                  <Chip size="small" clickable label="Last Friday" onClick={() => setPeriodEnd(lastFridayOrToday())} />
                </>
              ) : (
                <>
                  <Chip size="small" clickable label="End of this month" onClick={() => setPeriodEnd(monthEnd(0))} />
                  <Chip size="small" clickable label="End of last month" onClick={() => setPeriodEnd(monthEnd(-1))} />
                </>
              )}
            </Stack>
          </Box>
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <FormControl fullWidth>
            <InputLabel>Campaign</InputLabel>
            <Select label="Campaign" value={campaignId} onChange={(e) => { setCampaignId(String(e.target.value)); setChosen([]); }}>
              <MenuItem value="ALL">All campaigns</MenuItem>
              {campaigns.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControlLabel
            sx={{ whiteSpace: 'nowrap' }}
            control={<Checkbox checked={customerPaid} onChange={(e) => setCustomerPaid(e.target.checked)} />}
            label="Paid by the customer"
          />
        </Stack>
        {customerPaid && (
          <Typography variant="caption" color="text.secondary">
            Customer-paid: the supervisor confirms the customer approved it, and Accounting confirms the customer paid TEKS, each with a date.
          </Typography>
        )}

        <TextField label="General concept (optional)" value={concept} onChange={(e) => setConcept(e.target.value)} />
        {campaignId !== 'ALL' && (
          <FormControlLabel
            control={<Checkbox checked={everyone} onChange={(e) => setEveryone(e.target.checked)} />}
            label="Show every employee, not only the ones assigned to this campaign"
          />
        )}

        <Autocomplete
          multiple
          disableCloseOnSelect
          options={options}
          value={chosen}
          getOptionLabel={employeeLabel}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          filterOptions={(x) => x}
          onInputChange={(_e, value, reason) => { if (reason === 'input') void search(value); }}
          onChange={(_e, list) => onEmployees(list)}
          renderOption={(props, option, { selected }) => (
            <li {...props} key={option.id}><Checkbox size="small" checked={selected} sx={{ mr: 1 }} />{employeeLabel(option)}</li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Employees that qualify"
              helperText={campaignId === 'ALL' || everyone
                ? 'Tick every employee that qualifies.'
                : `Showing employees assigned to this campaign ${range ? `from ${range.start} to ${range.end}` : 'in the last 90 days'}.`}
            />
          )}
        />

        {chosen.map((u) => (
          <Stack key={u.id} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
            <Typography sx={{ minWidth: 220 }}>{employeeLabel(u)}</Typography>
            <TextField size="small" type="number" label={`Amount${category ? ` (${category.currency})` : ''}`} sx={{ width: 150 }}
              value={lineAmount[u.id] ?? ''} onChange={(e) => setLineAmount((p) => ({ ...p, [u.id]: e.target.value }))} />
            <TextField size="small" fullWidth label="Why this employee gets it (reason)" value={lineConcept[u.id] ?? ''}
              onChange={(e) => setLineConcept((p) => ({ ...p, [u.id]: e.target.value }))} />
          </Stack>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" disabled={!valid || saving} onClick={save}>Add bonus</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddBonusDialog;
